import { Chat, LinkPreviewResponse, PinnedChat, ThreadChat } from '@src/api/shared/community/chatApiClient';
import { ChannelMemberUserTypeEnum } from '@src/enum/ChannelMemberUserTypeEnum';
import React from 'react';

import { ThreadLink } from '../thread';
import ChatDeletedMessage from './ChatDeletedMessage';
import { ChatItemWrapper } from './ChatItemWrapper';
import ChatMessage, { ChatMenuPermission } from './ChatMessage';
import ChatUnreadLine from './ChatUnreadLine';

interface ChatCardProps {
  chat: Chat;
  pinnedChat: PinnedChat | null;
  linkPreviewCache: Record<string, LinkPreviewResponse | null>;
  isLoggedIn: boolean;
  currentUserId: number | null | undefined;
  currentUserType: ChannelMemberUserTypeEnum | null;
  editingChatId: number | null;
  isActionMenuSpOpen: boolean;
  currentChatIdSp: number | undefined;
  isMobile: boolean;
  shouldShowUnreadLine: (chatId: number) => boolean;
  onPinClick: (chat_id: number | undefined) => void;
  onChatEditClick: (chat: Chat | ThreadChat) => void;
  onChatDeleteClick: (chat: Chat | ThreadChat) => void;
  onChatReportClick: (chat: Chat | ThreadChat) => void;
  onPinnedChatMenuClose: () => void;
  onSetCurrentChatIdSp: (chatId: number | undefined) => void;
  onPinnedChatLongPress: () => void;
  onToggleLinkPreview: (chatId: number, hidePreview: boolean) => void;
  onOpenThread: (chatId: number) => void;
  getChatMenuPermission: (chat: Chat | ThreadChat) => ChatMenuPermission;
  chatRef: (el: HTMLDivElement | null) => void;
  chatBoxWidth: number;
}

export const ChatCard: React.FC<ChatCardProps> = ({
  chat,
  pinnedChat,
  linkPreviewCache,
  isLoggedIn,
  currentUserId,
  currentUserType,
  editingChatId,
  isActionMenuSpOpen,
  currentChatIdSp,
  isMobile,
  shouldShowUnreadLine,
  onPinClick,
  onChatEditClick,
  onChatDeleteClick,
  onChatReportClick,
  onPinnedChatMenuClose,
  onSetCurrentChatIdSp,
  onPinnedChatLongPress,
  onToggleLinkPreview,
  onOpenThread,
  getChatMenuPermission,
  chatRef,
  chatBoxWidth,
}) => {
  return (
    <div key={chat.id}>
      <ChatUnreadLine
        chat={chat}
        shouldShowUnreadLine={shouldShowUnreadLine}
      />
      <ChatItemWrapper
        isEditing={editingChatId === chat.id}
        isPinned={pinnedChat?.id === chat.id}
        isMobile={isMobile}
      >
        {chat.is_trash_displayed ? (
          <div ref={chatRef}>
            <ChatDeletedMessage />
          </div>
        ) : (
          <ChatMessage
            contextData={{
              chat,
              pinnedChat: pinnedChat ?? undefined,
              linkPreviewCache,
            }}
            userState={{
              isLoggedIn,
              currentUserId: currentUserId ?? undefined,
              currentUserType: currentUserType ?? undefined,
            }}
            uiState={{
              editingChatId,
              isActionMenuSpOpen,
              currentChatIdSp,
            }}
            callbacks={{
              onPinClick: async (chatId?: number) => {
                onPinClick(chatId);
              },
              onChatEditClick: onChatEditClick,
              onChatDeleteClick: onChatDeleteClick,
              onChatReportClick: onChatReportClick,
              onPinnedChatMenuClose: onPinnedChatMenuClose,
              onSetCurrentChatIdSp: onSetCurrentChatIdSp,
              onPinnedChatLongPress: onPinnedChatLongPress,
              onToggleLinkPreview: onToggleLinkPreview,
              onOpenThread: onOpenThread,
            }}
            getChatMenuPermission={getChatMenuPermission}
            chatRef={chatRef}
          />
        )}

        {/* Thread Reply Counter */}
        <div className='px-6 pb-2'>
          <ThreadLink
            replyCount={chat.threads?.count ?? 0}
            replyUsers={chat.threads?.users ?? []}
            chatBoxWidth={chatBoxWidth}
            lastReplyTime={chat.threads?.latest_created_at ?? ''}
            onClick={() => onOpenThread(chat.id)}
          />
        </div>
      </ChatItemWrapper>
    </div>
  );
};
